import { Injectable } from '@angular/core';
import { ExplorerFolder } from '@app/shared/types/models/files/explorer-folder.model';
import { FileDTO } from '../dtos/file/file.dto';
import { FolderDTO, FolderFileDTO } from '../dtos/folders/folder.dto';
import { AbstractFactory } from './abstract.factory';

/** Converts V3 metadata without creating a second cache of files. */
@Injectable({ providedIn: 'root' })
export class FolderFactory extends AbstractFactory<FolderDTO, ExplorerFolder> {
  public produce(dto: FolderDTO): ExplorerFolder {
    return new ExplorerFolder({
      id: dto.id,
      name: dto.name,
      parentFolderId: dto.parentFolderId ?? undefined,
      subFolderIds: dto.subFolders?.map(folder => folder.id) ?? [],
      createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
      createdById: dto.createdBy,
      type: this.folderType(dto.type),
      systemKey: this.systemKey(dto.systemKey),
      // Missing flags from older APIs must not enable destructive actions.
      isSystem: dto.isSystem ?? true,
      fileCapabilities: Object.fromEntries(
        Object.entries(dto.fileCapabilities ?? {}).map(([id, capability]) => [
          id,
          {
            canRename: capability.canRename === true,
            canMove: capability.canMove === true,
            canDelete: capability.canDelete === true
          }
        ])
      ),
      externalFolderUrl: dto.externalFolderUrl ?? undefined,
      canManageExternalFolder: dto.canManageExternalFolder === true,
      canCreateFolder: dto.canCreateFolder === true,
      canUploadFiles: dto.canUploadFiles === true,
      canRename: dto.canRename === true && dto.isSystem === false,
      canDelete: dto.canDelete === true && dto.isSystem === false
    });
  }

  public produceFileDTO(dto: FolderFileDTO): FileDTO {
    if (!dto.fileInFolderId || dto.fileInFolderId === '00000000-0000-0000-0000-000000000000') {
      throw new Error('The folder API did not provide an attachment ID');
    }
    return {
      id: dto.fileInFolderId,
      name: dto.name,
      size: dto.size,
      type: dto.type,
      orderNo: dto.orderNo,
      fileShortId: dto.fileShortId,
      createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
      createdBy: dto.createdBy,
      description: dto.description ?? undefined,
      descriptionUpdatedById: dto.descriptionUpdatedById ?? undefined,
      descriptionUpdatedOn: dto.descriptionUpdatedOn ? new Date(dto.descriptionUpdatedOn) : undefined
    };
  }
  private folderType(value: number | string | undefined): number | undefined {
    if (typeof value === 'number') {
      return value;
    }
    // Program.cs serializes enums as names; tolerate numeric responses from older hosts as well.
    const types: Record<string, number> = {
      Company: 0,
      Project: 1,
      Task: 2,
      User: 3,
      TimeReport: 4,
      TaskComment: 5,
      Location: 6,
      TaskNote: 10,
      Projects: 11,
      Tasks: 12,
      TaskNotes: 13,
      TimeReports: 14,
      TaskComments: 15,
      Locations: 16,
      TaskDescription: 17,
      TaskTimers: 18,
      TaskTimer: 19,
      TaskTimerEntries: 20,
      TaskTimerEntry: 21,
      TaskDescriptionEntry: 22,
      WorkTimeRegistration: 23,
      Organization: 24
    };
    return value === undefined ? undefined : types[value];
  }

  private systemKey(value: number | string | null | undefined): number | undefined {
    if (typeof value === 'number') {
      return value;
    }
    const keys: Record<string, number> = { Description: 1, Reports: 2, Comments: 3, Photos: 4, Documents: 5, Other: 6 };
    return value == null ? undefined : keys[value];
  }
}
